import React from 'react';
import { motion } from 'motion/react';
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SocialShare } from '@/components/ui/SocialShare';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useRegion } from '@/contexts/RegionContext';
import { LazyImage } from '@/components/ui/LazyImage';

export function LensEnergyCentresPage() {
  const { t } = useTranslation();
  const { regionCode } = useRegion();

  const title = 'Energy Centers Lens | Instinctual, Emotional & Intellectual | FIVE LENS';
  const description = 'Explore how individuals process information and make decisions using three core centres. The Energy Centers lens completes the FIVE LENS framework.';

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[
          { label: t('nav.framework', 'The Framework'), href: '/#framework' },
          { label: t('framework.lens5.title', 'Energy Centres') }
        ]} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="mb-6 flex items-center gap-3">
            <span className="h-[1px] w-8 bg-rule-3"></span>
            <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-rule-3">Lens 05</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-serif leading-tight mb-8 text-ink font-normal">
            {t('framework.lens5.title', 'Energy Centres')}
          </h1>
          
          <div className="prose prose-lg prose-brand max-w-none text-ink/70/80 leading-relaxed font-light">
            <p className="text-xl mb-4 font-normal text-ink">
              The Energy Centres lens explores how individuals process information and make decisions using three core centres: Instinctual, Emotional and Intellectual. Each centre represents a distinct form of intelligence, and our behaviour often reflects the dominance — or neglect — of one over the others.
            </p>
            <p className="mb-8">
              This lens brings awareness to how we respond in real time. It shows whether someone tends to over-rely on one centre or underuse another, and it highlights how integrating all three leads to more conscious, balanced action.
            </p>
            
            <LazyImage 
              src="/energy-centres-DETAILS.webp" 
              alt="Energy Centers lens diagram showing Instinctual, Emotional, and Intellectual processing centres"
              className="w-full max-w-md mx-auto my-12 object-contain mix-blend-multiply"
            />
            
            <h3 className="text-2xl font-serif text-ink mb-4">How This Lens Fits Into the FIVE LENS Framework</h3>
            <p className="mb-4">
              The Enneagram shows how people behave; the Energy Centres lens helps explain how those behaviours are decided in the moment. While most models focus on traits, this lens adds a dynamic, embodied layer to understanding real-time reactions.
            </p>
            <p className="mb-8">
              In the FIVE LENS model, Energy Centres complement the other lenses by revealing the interplay between cognition, feeling, and instinct. It’s especially useful in leadership development, communication training, and conflict resolution — where misalignment between the centres often leads to indecision or misjudgement.
            </p>

            <h3 className="text-2xl font-serif text-ink mb-4">How It Supports Coaching & Organisational Development</h3>
            <p className="mb-4">
              This lens helps individuals and teams understand their default decision-making style — whether they lead with logic, emotion, or instinct — and how that affects outcomes. For coaches, it offers a practical way to help clients notice and shift how they engage with situations under stress or uncertainty.
            </p>
            <p className="mb-8">
              In organisations, the Energy Centres lens supports better collaboration and communication by encouraging balanced thinking. It’s also valuable in leadership training, where the ability to integrate instinctual, emotional, and intellectual intelligence is essential for effective action.
            </p>
            
            <div className="bg-white p-8 md:p-12 border border-ground-deep my-12">
              <h3 className="text-2xl font-serif text-ink mb-6">The Three Core Centres</h3>
              <p className="mb-6">Within the broader FIVE LENS framework, the Energy Centres provide a dynamic layer for understanding real-time reactions and how behaviors are decided in the moment. We evaluate the utilization of three distinct processing centers:</p>
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Instinctual Centre</h4>
                  <p className="text-sm">The domain of intuition, presence, and action. When effectively utilized, it provides decisive momentum, boundary setting, and practical execution. Over-reliance can lead to impulsivity or resistance to change.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Emotional Centre</h4>
                  <p className="text-sm">The domain of empathy, relationship, and connection. When effectively utilized, it builds trust, fosters collaboration, and navigates complex interpersonal dynamics. Over-reliance can lead to emotional reactivity or boundary issues.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Intellectual Centre</h4>
                  <p className="text-sm">The domain of reason, analysis, and foresight. When effectively utilized, this center provides objective clarity, strategic planning, and rational evaluation. Over-reliance can lead to analysis paralysis or detachment.</p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-serif text-ink mb-4">Aligning for Maximum Impact</h3>
            <p className="mb-8">Effective leadership requires the harmonious integration of all three centers. When one center is chronically suppressed or over-dominant, blind spots emerge. The FIVE LENS model helps individuals identify their default center and intentionally activate the others to achieve a state of holistic alignment.</p>

            <div className="space-y-6 mb-12">
              <div className="flex flex-col md:flex-row gap-4 pb-6 border-b border-ground-deep/50">
                <div className="md:w-1/3 font-bold text-rule-3">The Bulldozer (Instinctual Dominant)</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Jumps straight into action without considering long-term strategic implications (Intellectual) or the emotional impact the action will have on the broader team (Emotional).</p></div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 pb-6 border-b border-ground-deep/50">
                <div className="md:w-1/3 font-bold text-rule-3">The People-Pleaser (Emotional Dominant)</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Prioritizes team harmony over necessary conflict, potentially avoiding hard business decisions or failing to establish objective performance boundaries.</p></div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 pb-6 border-b border-ground-deep/50">
                <div className="md:w-1/3 font-bold text-rule-3">The Over-thinker (Intellectual Dominant)</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Requires mountains of data before moving forward, often missing the human element of change management or failing to act when rapid intuition is needed.</p></div>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="md:w-1/3 font-bold text-rule-3">The Integrated Leader</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Gathers relevant data (Intellectual), senses the impact on the team (Emotional), and takes decisive, grounded action (Instinctual). This alignment creates trust, clarity, and sustainable momentum.</p></div>
              </div>
            </div>
            

            <SocialShare title={`FIVE LENS Framework: ${t('framework.lens5.title', 'Energy Centres')}`} />
          </div>

          <div className="mt-16 pt-16 border-t border-ground-deep">
            <h3 className="text-2xl font-serif text-ink mb-8">Recommended Reading</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              <Link 
                to="/lens/personal-mastery" 
                className="group block p-8 bg-white border border-ground-deep hover:shadow-md hover:shadow-ink/5 hover:border-ink hover:-translate-y-1 transition-all"
              >
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-rule-3 mb-3">Lens 02</h4>
                <h5 className="text-xl font-serif text-ink mb-3 group-hover:text-rule-3 transition-colors">Personal Mastery & Maturity</h5>
                <p className="text-sm text-ink/70/70 mb-6 line-clamp-2 font-light">Explore an individual's capacity for self-awareness, emotional regulation, and personal accountability.</p>
                <div className="flex items-center text-xs font-bold uppercase tracking-widest text-ink group-hover:text-rule-3 transition-colors">
                  Explore Lens <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              
              <Link 
                to="/lens/emotional-resilience" 
                className="group block p-8 bg-white border border-ground-deep hover:shadow-md hover:shadow-ink/5 hover:border-ink hover:-translate-y-1 transition-all"
              >
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-rule-3 mb-3">Lens 03</h4>
                <h5 className="text-xl font-serif text-ink mb-3 group-hover:text-rule-3 transition-colors">Emotional Resilience</h5>
                <p className="text-sm text-ink/70/70 mb-6 line-clamp-2 font-light">Discover how we process strain and recover energy. A crucial measure of sustainability under pressure.</p>
                <div className="flex items-center text-xs font-bold uppercase tracking-widest text-ink group-hover:text-rule-3 transition-colors">
                  Explore Lens <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
